import React, { useState, useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import axiosInstance from '../../Axios';
import Toast from '../../services/Toast';
import { withApplication } from '../../services/PendingApplication';
import { useBrand } from '../../hooks/useBrand';
import ProductsTagline from '../products-tagline/ProductsTagline';
import './NewPasswordForm.css';

interface NewPasswordFormProps {
  userData: { id: number; email: string };
  otp: string;
  application?: string | null;
}

interface PasswordValidation {
  minLength: boolean;
  hasUppercase: boolean;
  hasLowercase: boolean;
  hasNumber: boolean;
  hasSpecial: boolean;
}

type RuleKey = keyof PasswordValidation;

// `label` is the checklist wording, `missing` is the plain-language wording used
// in the "Still need:" hint under the disabled button.
const PASSWORD_RULES: { key: RuleKey; label: string; missing: string }[] = [
  { key: 'minLength', label: 'At least 8 characters', missing: '8 characters or more' },
  { key: 'hasUppercase', label: 'One uppercase letter', missing: 'a capital letter (A-Z)' },
  { key: 'hasLowercase', label: 'One lowercase letter', missing: 'a small letter (a-z)' },
  { key: 'hasNumber', label: 'One number', missing: 'a number (0-9)' },
  { key: 'hasSpecial', label: 'One special character', missing: 'a special character (! ? @ #)' },
];

const NewPasswordForm: React.FC<NewPasswordFormProps> = ({ userData, otp, application }) => {
  const [password, setPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [isPasswordFocused, setIsPasswordFocused] = useState(false);
  const [isConfirmFocused, setIsConfirmFocused] = useState(false);
  const [showPassword, setShowPassword] = useState(false);
  const [showConfirmPassword, setShowConfirmPassword] = useState(false);
  const [loading, setLoading] = useState(false);
  const navigate = useNavigate();
  const { brand, isBranded, style: brandStyle } = useBrand();

  const validation: PasswordValidation = useMemo(() => ({
    minLength: password.length >= 8,
    hasUppercase: /[A-Z]/.test(password),
    hasLowercase: /[a-z]/.test(password),
    hasNumber: /[0-9]/.test(password),
    hasSpecial: /[^a-zA-Z0-9]/.test(password),
  }), [password]);

  const isPasswordValid = useMemo(() => {
    return Object.values(validation).every(v => v);
  }, [validation]);

  const passwordsMatch = password === confirmPassword && confirmPassword.length > 0;

  // Rules only turn red once the user has started typing — an untouched field is
  // "nothing wrong yet", not "you failed".
  const hasStartedTyping = password.length > 0;
  const metCount = PASSWORD_RULES.filter(rule => validation[rule.key]).length;
  const progressPercent = (metCount / PASSWORD_RULES.length) * 100;
  const missingRules = PASSWORD_RULES.filter(rule => !validation[rule.key]);

  // Plain-language reason the submit button is disabled, so the user never has to
  // guess what is still pending.
  const blockingHint = useMemo(() => {
    if (!hasStartedTyping) return 'Enter a new password to continue';
    if (missingRules.length > 0) {
      return `Still need: ${missingRules.map(rule => rule.missing).join(', ')}`;
    }
    if (!confirmPassword) return 'Re-type your new password to confirm it';
    // A mismatch already has its own red banner above the button — don't say it twice.
    return null;
  }, [hasStartedTyping, missingRules, confirmPassword]);

  const handleSubmit = async () => {
    if (!isPasswordValid) {
      Toast.error('Password does not meet all requirements');
      return;
    }

    if (!passwordsMatch) {
      Toast.error('Passwords do not match');
      return;
    }

    try {
      setLoading(true);
      const payload = {
        id: userData.id,
        otp: otp,
        password: password
      };
      const response = await axiosInstance.post('/auth/reset-password', payload);

      if (!response?.data?.status) {
        Toast.error('Something went wrong. Please try again later.');
        setLoading(false);
        return;
      }

      Toast.success('Password reset successfully! Please login with your new password.');
      // withApplication falls back to the remembered app, so the handoff still
      // works if the reset flow was reached without ?application= in the URL.
      navigate(application ? `/?application=${encodeURIComponent(application)}` : withApplication('/'));
    } catch (error: any) {
      setLoading(false);
    }
  };

  // Three states, never colour alone: pending (grey dot, nothing typed yet),
  // missing (red cross, still to do) and valid (green tick, done).
  const ValidationItem: React.FC<{ valid: boolean; text: string }> = ({ valid, text }) => {
    const state = valid ? 'valid' : hasStartedTyping ? 'missing' : 'pending';

    return (
      <div className={`validation-item validation-item--${state}`}>
        <span className="validation-item__badge" aria-hidden="true">
          <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="3" strokeLinecap="round" strokeLinejoin="round">
            {state === 'valid' && <polyline points="20 6 9 17 4 12"/>}
            {state === 'missing' && (
              <>
                <line x1="18" y1="6" x2="6" y2="18"/>
                <line x1="6" y1="6" x2="18" y2="18"/>
              </>
            )}
            {state === 'pending' && <circle cx="12" cy="12" r="4" fill="currentColor" stroke="none"/>}
          </svg>
        </span>
        <span className="validation-item__text">{text}</span>
        {state === 'missing' && <span className="validation-item__tag">Still needed</span>}
      </div>
    );
  };

  return (
    <div className="new-password-card" style={brandStyle} data-brand={brand.key || 'nextek'}>
      <div className="new-password-card__inner">
        {/* Decorative elements */}
        <div className="new-password-card__decoration">
          <div className="new-password-card__circle new-password-card__circle--1" />
          <div className="new-password-card__circle new-password-card__circle--2" />
        </div>

        {/* Icon */}
        <div className="new-password-card__icon">
          <div className="new-password-card__icon-bg">
            <svg width="48" height="48" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round">
              <path d="M21 2l-2 2m-7.61 7.61a5.5 5.5 0 1 1-7.778 7.778 5.5 5.5 0 0 1 7.777-7.777zm0 0L15.5 7.5m0 0l3 3L22 7l-3-3m-3.5 3.5L19 4"/>
            </svg>
          </div>
          <div className="new-password-card__check">
            <svg width="20" height="20" viewBox="0 0 24 24" fill="currentColor">
              <path d="M12 22s8-4 8-10V5l-8-3-8 3v7c0 6 8 10 8 10z"/>
            </svg>
          </div>
        </div>

        {/* Header */}
        <div className="new-password-card__header">
          <h1 className="new-password-card__title">
            <span className="new-password-card__title-accent">Create New</span> Password
          </h1>
          <p className="new-password-card__subtitle">
            Your new password must be different from previously used passwords
          </p>
        </div>

        {/* Form */}
        <form className="new-password-form" onSubmit={(e) => e.preventDefault()} data-cy="new-password-form">
          <div className={`new-password-input-group ${isPasswordFocused ? 'new-password-input-group--focused' : ''} ${password ? 'new-password-input-group--filled' : ''}`}>
            <div className="new-password-input-icon">
              <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                <rect x="3" y="11" width="18" height="11" rx="2" ry="2"/>
                <path d="M7 11V7a5 5 0 0 1 10 0v4"/>
              </svg>
            </div>
            <input
              type={showPassword ? 'text' : 'password'}
              value={password}
              onChange={(e) => setPassword(e.target.value)}
              placeholder="New Password"
              className="new-password-input"
              onFocus={() => setIsPasswordFocused(true)}
              onBlur={() => setIsPasswordFocused(false)}
              data-cy="new-password-input"
            />
            <button
              type="button"
              className="new-password-toggle"
              onClick={() => setShowPassword(!showPassword)}
              data-cy="new-password-toggle"
            >
              <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                {showPassword ? (
                  <>
                    <path d="M17.94 17.94A10.07 10.07 0 0 1 12 20c-7 0-11-8-11-8a18.45 18.45 0 0 1 5.06-5.94M9.9 4.24A9.12 9.12 0 0 1 12 4c7 0 11 8 11 8a18.5 18.5 0 0 1-2.16 3.19m-6.72-1.07a3 3 0 1 1-4.24-4.24"/>
                    <line x1="1" y1="1" x2="23" y2="23"/>
                  </>
                ) : (
                  <>
                    <path d="M1 12s4-8 11-8 11 8 11 8-4 8-11 8-11-8-11-8z"/>
                    <circle cx="12" cy="12" r="3"/>
                  </>
                )}
              </svg>
            </button>
            <div className="new-password-input-highlight" />
          </div>

          {/* Password Validation */}
          <div className={`password-validation ${isPasswordValid ? 'password-validation--complete' : ''}`}>
            <div className="password-validation__head">
              <span className="password-validation__title">Password requirements</span>
              <span className="password-validation__count" aria-live="polite">
                {metCount} of {PASSWORD_RULES.length} done
              </span>
            </div>

            <div
              className="password-validation__bar"
              role="progressbar"
              aria-valuemin={0}
              aria-valuemax={PASSWORD_RULES.length}
              aria-valuenow={metCount}
              aria-label="Password requirements met"
            >
              <span
                className="password-validation__bar-fill"
                style={{ width: `${progressPercent}%` }}
              />
            </div>

            <div className="password-validation__list">
              {PASSWORD_RULES.map(rule => (
                <ValidationItem key={rule.key} valid={validation[rule.key]} text={rule.label} />
              ))}
            </div>
          </div>

          <div className={`new-password-input-group ${isConfirmFocused ? 'new-password-input-group--focused' : ''} ${confirmPassword ? 'new-password-input-group--filled' : ''} ${confirmPassword && !passwordsMatch ? 'new-password-input-group--error' : ''}`}>
            <div className="new-password-input-icon">
              <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                <path d="M22 11.08V12a10 10 0 1 1-5.93-9.14"/>
                <polyline points="22 4 12 14.01 9 11.01"/>
              </svg>
            </div>
            <input
              type={showConfirmPassword ? 'text' : 'password'}
              value={confirmPassword}
              onChange={(e) => setConfirmPassword(e.target.value)}
              placeholder="Confirm Password"
              className="new-password-input"
              onFocus={() => setIsConfirmFocused(true)}
              onBlur={() => setIsConfirmFocused(false)}
              data-cy="confirm-password-input"
            />
            <button
              type="button"
              className="new-password-toggle"
              onClick={() => setShowConfirmPassword(!showConfirmPassword)}
              data-cy="confirm-password-toggle"
            >
              <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                {showConfirmPassword ? (
                  <>
                    <path d="M17.94 17.94A10.07 10.07 0 0 1 12 20c-7 0-11-8-11-8a18.45 18.45 0 0 1 5.06-5.94M9.9 4.24A9.12 9.12 0 0 1 12 4c7 0 11 8 11 8a18.5 18.5 0 0 1-2.16 3.19m-6.72-1.07a3 3 0 1 1-4.24-4.24"/>
                    <line x1="1" y1="1" x2="23" y2="23"/>
                  </>
                ) : (
                  <>
                    <path d="M1 12s4-8 11-8 11 8 11 8-4 8-11 8-11-8-11-8z"/>
                    <circle cx="12" cy="12" r="3"/>
                  </>
                )}
              </svg>
            </button>
            <div className="new-password-input-highlight" />
          </div>

          {confirmPassword && !passwordsMatch && (
            <div className="password-mismatch">
              <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                <circle cx="12" cy="12" r="10"/>
                <line x1="15" y1="9" x2="9" y2="15"/>
                <line x1="9" y1="9" x2="15" y2="15"/>
              </svg>
              <span>Passwords do not match</span>
            </div>
          )}

          {confirmPassword && passwordsMatch && (
            <div className="password-match">
              <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
                <polyline points="20 6 9 17 4 12"/>
              </svg>
              <span>Passwords match</span>
            </div>
          )}

          <button
            type="button"
            disabled={loading || !isPasswordValid || !passwordsMatch}
            onClick={handleSubmit}
            className={`new-password-btn ${loading ? 'new-password-btn--loading' : ''}`}
            data-cy="new-password-submit-btn"
          >
            {loading ? (
              <div className="new-password-btn__spinner">
                <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                  <circle cx="12" cy="12" r="10" strokeOpacity="0.25"/>
                  <path d="M12 2a10 10 0 0 1 10 10" strokeLinecap="round"/>
                </svg>
              </div>
            ) : (
              <>
                Reset Password
                <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                  <path d="M5 12h14"/>
                  <path d="m12 5 7 7-7 7"/>
                </svg>
              </>
            )}
          </button>

          {!loading && blockingHint && (
            <div className="new-password-hint" aria-live="polite" data-cy="new-password-hint">
              <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                <circle cx="12" cy="12" r="10"/>
                <line x1="12" y1="8" x2="12" y2="12"/>
                <line x1="12" y1="16" x2="12.01" y2="16"/>
              </svg>
              <span>{blockingHint}</span>
            </div>
          )}
        </form>

        {/* Footer */}
        <div className="new-password-footer">
          <ProductsTagline signingInTo={isBranded ? brand.name : null} />
        </div>
      </div>
    </div>
  );
};

export default NewPasswordForm;
